import React, { useState } from 'react';
import { uploadToCloudinary, validateFile } from '../utils/cloudinary';
import './FileUpload.css';

const FileUpload = ({
  onUpload,
  currentUrl,
  label = 'Upload File',
  accept = 'image/*,application/pdf',
  showPreview = true,
}) => {
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [preview, setPreview] = useState(() => {
    if (currentUrl) {
      const isPDF = currentUrl.toLowerCase().includes('.pdf');
      return {
        type: isPDF ? 'pdf' : 'image',
        url: currentUrl,
        name: isPDF ? 'Existing PDF' : 'Existing Image',
      };
    }
    return null;
  });

  // ✅ Handle file selection
  const handleFileSelect = async (file) => {
    if (!file) return;

    // Validate file before upload
    const validation = validateFile(file);
    if (!validation.valid) {
      alert(validation.error);
      return;
    }

    setUploading(true);

    // Show a quick preview for images (before upload completes)
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) =>
        setPreview({ type: 'image', url: e.target.result, name: file.name });
      reader.readAsDataURL(file);
    } else if (file.type === 'application/pdf') {
      setPreview({ type: 'pdf', name: file.name, size: file.size });
    }

    try {
      // 🔧 Upload to Cloudinary
      const result = await uploadToCloudinary(file);

      if (result.success) {
        onUpload(result.url);
        setPreview({
          type: file.type.startsWith('image/') ? 'image' : 'pdf',
          url: result.url,
          name: file.name,
          size: file.size,
        });
      } else {
        alert('❌ Upload failed: ' + result.error);
      }
    } catch (error) {
      alert('❌ Upload failed: ' + error.message);
    } finally {
      setUploading(false);
    }
  };

  // ✅ File input handler
  const handleFileInput = (e) => {
    const file = e.target.files[0];
    if (file) handleFileSelect(file);
  };

  // ✅ Drag & drop handlers
  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFileSelect(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const getUploadText = () => {
    if (accept.includes('image') && accept.includes('pdf')) {
      return {
        main: 'Drop images or PDFs here or click to upload',
        sub: 'Max 5MB • JPG, PNG, GIF, WebP, PDF',
      };
    } else if (accept.includes('image')) {
      return {
        main: 'Drop images here or click to upload',
        sub: 'Max 5MB • JPG, PNG, GIF, WebP',
      };
    } else if (accept.includes('pdf')) {
      return {
        main: 'Drop PDF here or click to upload',
        sub: 'Max 5MB • PDF files only',
      };
    }
    return { main: 'Drop files here or click to upload', sub: 'Max 5MB' };
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    const k = 1024;
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    return `${(bytes / Math.pow(k, i)).toFixed(2)} ${sizes[i]}`;
  };

  // ✅ UI Rendering
  return (
    <div className="file-upload-container">
      <label className="file-upload-label">{label}</label>

      <div
        className={`upload-dropzone ${dragOver ? 'drag-over' : ''} ${uploading ? 'uploading' : ''}`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
      >
        <input
          type="file"
          accept={accept}
          onChange={handleFileInput}
          disabled={uploading}
          className="file-input"
        />

        <div className="upload-content">
          {uploading ? (
            <div className="upload-progress">
              <div className="spinner"></div>
              <span>Uploading...</span>
            </div>
          ) : (
            <>
              <div className="upload-icon">📁</div>
              <div className="upload-text">
                <span className="upload-main">{getUploadText().main}</span>
                <span className="upload-sub">{getUploadText().sub}</span>
              </div>
            </>
          )}
        </div>
      </div>

      {showPreview && preview && (
        <div className="file-preview">
          {preview.type === 'pdf' ? (
            <div className="pdf-preview">
              <div className="pdf-icon">📄</div>
              <div className="pdf-info">
                <span className="pdf-name">{preview.name}</span>
                {preview.size && <span className="pdf-size">{formatFileSize(preview.size)}</span>}
                {preview.url && (
                  <a href={preview.url} target="_blank" rel="noopener noreferrer" className="pdf-link">
                    View PDF
                  </a>
                )}
              </div>
            </div>
          ) : (
            <div className="image-preview-container">
              <img src={preview.url} alt="Preview" className="image-preview" />
              <div className="image-info">
                <span className="image-name">{preview.name}</span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default FileUpload;
